"use client";

import { useLazyQuery } from "@apollo/client/react";
import { usePathname, useRouter } from "next/navigation";
import {
  useCallback,
  useContext,
  useEffect,
  useEffectEvent,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  LuGlobe,
  LuImage,
  LuLayers,
  LuPin,
  LuUsers as LuUsers2,
} from "react-icons/lu";

import { CollectionsPage } from "@/app/(app)/collections/[userPublicKey]/collections-page";
import TokenDetails from "@/components/app/profile/token-details";
import { EmptyState } from "@/components/shared/empty-state";
import { Spinner } from "@/components/shared/spinner";
import {
  SurfaceSelectorRow,
  type SurfaceSelectorRowItem,
} from "@/components/shared/surface-selector-row";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@/components/ui/select";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { PostType } from "@/contexts/feed-context";
import {
  FeedProviderInternal,
  useFeedContext,
  useFeedLogic,
} from "@/contexts/feed-context";
import { UserContext } from "@/contexts/user-context";
import {
  PostByPostHashDocument,
  UserAssociationsDocument,
} from "@/graphql/codegen/graphql";
import { useCoinPropertiesQuery } from "@/hooks/use-coin-properties-query";
import { useCollections } from "@/hooks/use-collections";
import { useMobile } from "@/hooks/use-mobile";
import { usePinnedUserTabLogic } from "@/hooks/use-pinned-user-tab-logic";
import type { GetPostsDTO } from "@/hooks-api/use-get-posts";
import { useGetPosts } from "@/hooks-api/use-get-posts";
import {
  useGetUserBlockedState,
  useGetViewerBlockedState,
} from "@/hooks-api/use-get-user-blocked-state";
import { APP_ROUTES, AssociationType } from "@/utils/constants";
import {
  feedFreezeDebugLog,
  getFeedFreezeDebugDuration,
} from "@/utils/feed-freeze-debug";
import { isAbortError } from "@/utils/helpers";
import { getUserName } from "@/utils/usernames";

import { Feed } from "../feed";
import { FeedBlockedUserContent } from "../feed-blocked-user-content";
import { getFeedFilters } from "../get-feed-filters";
import { FeedMedia } from "./feed-media";
import { FeedPostSkeletonList } from "./feed-post-skeleton-list";
import { UserFeedHeader } from "./feed-user-header";

export enum USER_PROFILE_TAB_ITEMS {
  TOKEN = "token",
  FEED = "feed",
  MEDIA = "media",
  COLLECTIONS = "collections",
}

export enum USER_PROFILE_TAB_ITEMS_SUBCATEGORIES {
  OVERVIEW = "overview",
  AMM = "amm",
  HOLDERS = "holders",
  PROPERTIES = "properties",
}

const profileLastActiveTabMemory = new Map<string, USER_PROFILE_TAB_ITEMS>();
const PINNED_PROFILE_TAB_TITLE = "Pinned profile tab";
const PINNED_PROFILE_TAB_DESCRIPTION =
  "Choose which tab opens first on your profile when no specific tab link is used.";

export const FeedUserWrapper = ({
  userPublicKey,
  username,
  userTab = "",
}: {
  userPublicKey?: string;
  username?: string;
  userTab?: string;
}) => {
  const { data: coinPropertiesData, isLoading: loadingCoinProperties } =
    useCoinPropertiesQuery({
      publicKey: userPublicKey as string,
    });

  const hasCoin =
    coinPropertiesData?.hasCoin && coinPropertiesData.hasAmmConfigs;
  const rememberedActiveTab = userPublicKey
    ? (profileLastActiveTabMemory.get(userPublicKey) ?? null)
    : null;

  const [activeTab, setActiveTab] = useState<USER_PROFILE_TAB_ITEMS | null>(
    () => {
      const explicitUserProfileTab =
        USER_PROFILE_TAB_ITEMS[
          userTab.toUpperCase() as keyof typeof USER_PROFILE_TAB_ITEMS
        ];

      if (
        explicitUserProfileTab &&
        explicitUserProfileTab !== USER_PROFILE_TAB_ITEMS.TOKEN
      ) {
        return explicitUserProfileTab;
      }

      if (rememberedActiveTab) {
        return rememberedActiveTab;
      }

      return null;
    },
  );
  const [userTokenSubTab, setUserTokenSubTab] =
    useState<USER_PROFILE_TAB_ITEMS_SUBCATEGORIES | null>(null);
  const [loading, setLoading] = useState(true);
  const { getPosts } = useGetPosts();
  const { isMobile } = useMobile();
  const router = useRouter();
  const pathname = usePathname();
  const { getFeedSnapshot, setFeedSnapshot } = useFeedContext();
  const [hasResolvedFeed, setHasResolvedFeed] = useState(false);
  const profileBootstrapInFlightKeyRef = useRef<string | null>(null);

  const feedLogic = useFeedLogic({
    enableAdminPinnedPostFilters: false,
  });
  const offset = feedLogic.posts.length;
  const {
    addUserToBlockedUsers,
    removeUserFromBlockedUsers,
    currentUserPublicKey,
  } = useContext(UserContext);

  const blockedUserData = useGetUserBlockedState({
    transactorPublicKey: currentUserPublicKey,
    userPublicKey,
  });

  const viewerBlockState = useGetViewerBlockedState({
    userPublicKey,
    viewerPublicKey: currentUserPublicKey,
  });

  const [getPinnedPostUserAssociation] = useLazyQuery(
    UserAssociationsDocument,
    {
      fetchPolicy: "network-only",
    },
  );
  const [getPostByPostHash] = useLazyQuery(PostByPostHashDocument, {
    fetchPolicy: "network-only",
  });

  const {
    initializePosts,
    rawPosts,
    resetFeed,
    setPinnedPost,
    setPinnedPostAssociationId,
  } = feedLogic;

  const getPinnedPost = useEffectEvent(async () => {
    try {
      const pinnedAssociationRequest = getPinnedPostUserAssociation({
        variables: {
          filter: {
            associationType: { equalTo: AssociationType.pin },
            target: {
              publicKey: {
                equalTo: userPublicKey,
              },
            },
            transactor: {
              publicKey: {
                equalTo: currentUserPublicKey,
              },
            },
          },
        },
      });
      pinnedAssociationRequest.retain?.();
      const { data: userPinnedAssociationPostData } =
        await pinnedAssociationRequest;

      const pinnedPostAssociationId =
        userPinnedAssociationPostData?.userAssociations?.nodes[0]
          ?.associationId ?? null;

      const pinnedPostHash =
        userPinnedAssociationPostData?.userAssociations?.nodes[0]?.extraData
          ?.postHash;

      if (pinnedPostHash) {
        const pinnedPostRequest = getPostByPostHash({
          variables: {
            postHash: pinnedPostHash,
          },
        });
        pinnedPostRequest.retain?.();
        const { data: pinnedPostData } = await pinnedPostRequest;

        const pinnedPost = pinnedPostData?.postByPostHash as PostType;
        setPinnedPostAssociationId(pinnedPostAssociationId);
        setPinnedPost(pinnedPost);
      }
    } catch (error) {
      if (!isAbortError(error)) {
        console.error("Error fetching pinned post", error);
      }
    }
  });

  const fetchPosts = useEffectEvent(async () => {
    const filters = getFeedFilters({
      userPublicKey,
      postCategoryFilter: feedLogic.postCategoryFilter,
    });
    const startedAt = performance.now();

    feedFreezeDebugLog("profile-feed", "fetch:start", {
      offset: 0,
      postCategoryFilter: feedLogic.postCategoryFilter,
      postOrderBy: feedLogic.postOrderBy,
      userPublicKey: userPublicKey ?? null,
    });

    await getPinnedPost();
    const data = await getPosts({
      filters: {
        ...filters,
        isHidden: {
          equalTo: false,
        },
      },
      offset: 0,
      orderBy: feedLogic.postOrderBy,
    });

    const posts = data?.posts?.nodes.filter(Boolean) ?? [];
    feedFreezeDebugLog("profile-feed", "fetch:complete", {
      count: posts.length,
      durationMs: getFeedFreezeDebugDuration(startedAt),
      firstPostHash: posts[0]?.postHash ?? null,
      lastPostHash: posts.at(-1)?.postHash ?? null,
      offset: 0,
    });
    initializePosts(posts);
  });

  const onUserBlock = useCallback(
    async (publicKey: string) => {
      setLoading(true);
      try {
        addUserToBlockedUsers(publicKey);
        await blockedUserData.refetch();
      } catch (error) {
        removeUserFromBlockedUsers(publicKey);
        console.error("Error blocking user", error);
      } finally {
        setLoading(false);
      }
    },
    [addUserToBlockedUsers, blockedUserData, removeUserFromBlockedUsers],
  );

  const onUserUnblock = useCallback(
    async (publicKey: string) => {
      try {
        window.location.reload();
      } catch (error) {
        addUserToBlockedUsers(publicKey);
        console.error("Error unblocking user", error);
      } finally {
        setLoading(false);
      }
    },
    [addUserToBlockedUsers],
  );

  const getPostsRequest = useCallback(
    async (params: GetPostsDTO) => {
      const data = await getPosts(params);
      return {
        posts: data?.posts?.nodes.filter(Boolean) || [],
      };
    },
    [getPosts],
  );
  const profileFeedFiltersKey = Object.entries(feedLogic.postCategoryFilter)
    .sort(([leftKey], [rightKey]) => leftKey.localeCompare(rightKey))
    .map(([key, value]) => `${key}:${value ? 1 : 0}`)
    .join(",");
  const profileFeedScrollRestorationKey = `profile-feed:${
    userPublicKey ?? "unknown"
  }:${feedLogic.postOrderBy}:${profileFeedFiltersKey}`;
  const profileFeedSnapshot = getFeedSnapshot(profileFeedScrollRestorationKey);
  const shouldWaitForPinnedTab = !userTab && !rememberedActiveTab;
  const shouldWaitForCoinProperties =
    (!userTab && !rememberedActiveTab) ||
    userTab === USER_PROFILE_TAB_ITEMS.TOKEN ||
    USER_PROFILE_TAB_ITEMS_SUBCATEGORIES[
      userTab.toUpperCase() as keyof typeof USER_PROFILE_TAB_ITEMS_SUBCATEGORIES
    ] !== undefined;

  const renderFeedContent = useCallback(() => {
    if (
      !offset &&
      (loading || blockedUserData.loading || viewerBlockState.loading)
    ) {
      return <FeedPostSkeletonList count={10} withLeadingBorder />;
    }

    if (blockedUserData?.blocked) {
      const { blockedUserPublicKey, assosiationId } = blockedUserData;

      return (
        <FeedBlockedUserContent
          {...{ onUserUnblock, blockedUserPublicKey, assosiationId }}
        />
      );
    }

    if (viewerBlockState.viewerIsBlocked) {
      return (
        <div
          className="
            flex flex-col items-center justify-center gap-4 border-y p-12
            text-center
          "
        >
          <EmptyState
            title="You are unable to view this user's posts because you have been blocked by them."
            image="/assets/empty-12.png"
          />
        </div>
      );
    }

    if (!offset) {
      return (
        <div className="py-32">
          <EmptyState
            image="/assets/empty-24.png"
            title={`No posts from ${getUserName({
              account: {
                username,
                publicKey: userPublicKey,
              },
            })}`}
            description={`${getUserName({
              account: {
                username,
                publicKey: userPublicKey,
              },
            })} has not posted any content yet.`}
            buttonText="Find Active Users"
            onClick={() => {
              router.push(APP_ROUTES.discover);
            }}
          />
        </div>
      );
    }

    return (
      <Feed
        {...{
          getPosts: getPostsRequest,
          scrollRestorationKey: profileFeedScrollRestorationKey,
          userPublicKey,
        }}
      />
    );
  }, [
    loading,
    blockedUserData,
    viewerBlockState.loading,
    viewerBlockState.viewerIsBlocked,
    offset,
    getPostsRequest,
    profileFeedScrollRestorationKey,
    userPublicKey,
    onUserUnblock,
    username,
    router,
  ]);

  const { setPinnedTabRequest, pinnedTabLoading, pinnedTab } =
    usePinnedUserTabLogic(userPublicKey);
  const { collections } = useCollections({
    userPublicKey: userPublicKey as string,
  });
  const profileTabItems = useMemo(() => {
    const items: SurfaceSelectorRowItem<USER_PROFILE_TAB_ITEMS>[] = [];

    if (hasCoin) {
      items.push({
        icon: LuUsers2,
        key: USER_PROFILE_TAB_ITEMS.TOKEN,
        label: "Token",
        value: USER_PROFILE_TAB_ITEMS.TOKEN,
      });
    }

    items.push(
      {
        icon: LuGlobe,
        key: USER_PROFILE_TAB_ITEMS.FEED,
        label: "Feed",
        value: USER_PROFILE_TAB_ITEMS.FEED,
      },
      {
        icon: LuImage,
        key: USER_PROFILE_TAB_ITEMS.MEDIA,
        label: "Media",
        value: USER_PROFILE_TAB_ITEMS.MEDIA,
      },
    );

    if (collections?.length) {
      items.push({
        icon: LuLayers,
        key: USER_PROFILE_TAB_ITEMS.COLLECTIONS,
        label: "Collections",
        value: USER_PROFILE_TAB_ITEMS.COLLECTIONS,
      });
    }

    return items;
  }, [collections?.length, hasCoin]);

  useLayoutEffect(() => {
    if (!userPublicKey) return;

    const snapshot = getFeedSnapshot(profileFeedScrollRestorationKey);
    profileBootstrapInFlightKeyRef.current = null;

    if (snapshot?.resolved) {
      initializePosts(snapshot.rawPosts);
      setPinnedPost(snapshot.pinnedPost);
      setPinnedPostAssociationId(snapshot.pinnedPostAssociationId);
      setHasResolvedFeed(true);
      setLoading(false);
      return;
    }

    resetFeed();
    setPinnedPost(null);
    setPinnedPostAssociationId(null);
    setHasResolvedFeed(false);
  }, [
    getFeedSnapshot,
    initializePosts,
    profileFeedScrollRestorationKey,
    resetFeed,
    setPinnedPost,
    setPinnedPostAssociationId,
    userPublicKey,
  ]);

  useEffect(() => {
    if (activeTab !== USER_PROFILE_TAB_ITEMS.FEED || !hasResolvedFeed) return;

    setFeedSnapshot(profileFeedScrollRestorationKey, {
      pinnedPost: feedLogic.pinnedPost,
      pinnedPostAssociationId: feedLogic.pinnedPostAssociationId,
      rawPosts,
      resolved: true,
    });
  }, [
    activeTab,
    feedLogic.pinnedPost,
    feedLogic.pinnedPostAssociationId,
    hasResolvedFeed,
    profileFeedScrollRestorationKey,
    rawPosts,
    setFeedSnapshot,
  ]);

  useEffect(() => {
    if (!userPublicKey || !activeTab) return;

    profileLastActiveTabMemory.set(userPublicKey, activeTab);
  }, [activeTab, userPublicKey]);

  useEffect(() => {
    if (!activeTab || userTab) return;

    const currentSegments = pathname.split("/").filter(Boolean);
    const currentProfileSegment = currentSegments[0];

    if (!currentProfileSegment) return;

    const targetPath = `/${currentProfileSegment}/${activeTab}`;

    if (pathname !== targetPath) {
      router.replace(targetPath, {
        scroll: false,
      });
    }
  }, [activeTab, pathname, router, userTab]);

  useEffect(() => {
    const run = async () => {
      if (!userPublicKey || activeTab !== USER_PROFILE_TAB_ITEMS.FEED) return;
      if (profileFeedSnapshot?.resolved) {
        setLoading(false);
        return;
      }
      if (
        profileBootstrapInFlightKeyRef.current ===
        profileFeedScrollRestorationKey
      ) {
        return;
      }

      profileBootstrapInFlightKeyRef.current = profileFeedScrollRestorationKey;
      setLoading(true);
      try {
        await fetchPosts();
        setHasResolvedFeed(true);
      } finally {
        if (
          profileBootstrapInFlightKeyRef.current ===
          profileFeedScrollRestorationKey
        ) {
          profileBootstrapInFlightKeyRef.current = null;
        }
        setLoading(false);
      }
    };

    void run();
  }, [
    activeTab,
    fetchPosts,
    profileFeedScrollRestorationKey,
    profileFeedSnapshot?.resolved,
    userPublicKey,
  ]);

  const effectiveUserTab = userTab || rememberedActiveTab || pinnedTab || "";

  useEffect(() => {
    if (
      (shouldWaitForCoinProperties && loadingCoinProperties) ||
      (shouldWaitForPinnedTab && pinnedTabLoading)
    ) {
      return;
    }

    const tokenSubTab =
      USER_PROFILE_TAB_ITEMS_SUBCATEGORIES[
        effectiveUserTab.toUpperCase() as keyof typeof USER_PROFILE_TAB_ITEMS_SUBCATEGORIES
      ];
    const userProfileTab =
      USER_PROFILE_TAB_ITEMS[
        effectiveUserTab.toUpperCase() as keyof typeof USER_PROFILE_TAB_ITEMS
      ];

    const finalUserTab = (() => {
      if (
        (hasCoin && tokenSubTab) ||
        (hasCoin && !tokenSubTab && !userProfileTab)
      ) {
        return USER_PROFILE_TAB_ITEMS.TOKEN;
      }

      if (!hasCoin && userProfileTab === USER_PROFILE_TAB_ITEMS.TOKEN) {
        return USER_PROFILE_TAB_ITEMS.FEED;
      }

      return userProfileTab || USER_PROFILE_TAB_ITEMS.FEED;
    })();

    const userTokenSubTab = hasCoin
      ? tokenSubTab || USER_PROFILE_TAB_ITEMS_SUBCATEGORIES.OVERVIEW
      : null;

    setActiveTab(finalUserTab as USER_PROFILE_TAB_ITEMS);
    setUserTokenSubTab(userTokenSubTab);
  }, [
    effectiveUserTab,
    hasCoin,
    loadingCoinProperties,
    pinnedTabLoading,
    shouldWaitForCoinProperties,
    shouldWaitForPinnedTab,
  ]);

  const syncURLWithActiveTab = (tabValue: string) => {
    const url = new URL(window.location.href);

    const username = window.location.pathname
      .split("/")
      .filter(Boolean)
      .shift();

    url.pathname = `${username}/${tabValue}`;
    router.push(url.pathname, {
      scroll: false,
    });
  };

  const handleTabChange = (tabValue: USER_PROFILE_TAB_ITEMS) => {
    setActiveTab(tabValue);

    syncURLWithActiveTab(tabValue);
  };

  const handlePinnedTabChange = (tabValue: USER_PROFILE_TAB_ITEMS) => {
    if (userPublicKey === currentUserPublicKey) {
      void setPinnedTabRequest(tabValue);
    }
  };

  const renderPinnedTabSelect = () => {
    if (userPublicKey !== currentUserPublicKey) return null;

    const pinnedTabTrigger = (
      <SelectTrigger
        aria-label="Choose pinned profile tab"
        showIcon={false}
        className="
          size-9 justify-center rounded-full bg-transparent p-0
          text-foreground
          hover:bg-card hover:text-muted-foreground
        "
      >
        <LuPin className="size-4" />
      </SelectTrigger>
    );

    return (
      <Select<USER_PROFILE_TAB_ITEMS>
        value={pinnedTab}
        onValueChange={handlePinnedTabChange}
        disabled={pinnedTabLoading}
        drawerTitle={PINNED_PROFILE_TAB_TITLE}
        historyName="profile-pinned-tab"
      >
        {isMobile ? (
          pinnedTabTrigger
        ) : (
          <Tooltip>
            <TooltipTrigger
              render={pinnedTabTrigger}
              delay={50}
              closeDelay={0}
            />
            <TooltipContent className="max-w-[240px]">
              <div className="mb-1 font-sans font-semibold text-foreground">
                {PINNED_PROFILE_TAB_TITLE}
              </div>
              <div>{PINNED_PROFILE_TAB_DESCRIPTION}</div>
            </TooltipContent>
          </Tooltip>
        )}
        <SelectContent
          align="end"
          className="min-w-44"
          drawerTitle={PINNED_PROFILE_TAB_TITLE}
          drawerDescription={PINNED_PROFILE_TAB_DESCRIPTION}
        >
          <div className="px-1 pb-3 md:hidden">
            <div className="font-sans text-sm font-semibold text-foreground">
              {PINNED_PROFILE_TAB_TITLE}
            </div>
            <p className="mt-1 text-sm text-muted">
              {PINNED_PROFILE_TAB_DESCRIPTION}
            </p>
          </div>

          {profileTabItems.map((item) => {
            const Icon = item.icon;

            return (
              <SelectItem key={item.key} value={item.value}>
                <span className="flex min-w-0 items-center gap-2">
                  <Icon className="size-4 shrink-0" />
                  <span className="truncate">{item.label}</span>
                </span>
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>
    );
  };

  return (
    <FeedProviderInternal {...feedLogic} {...{ onUserBlock }}>
      {(shouldWaitForCoinProperties && loadingCoinProperties) ||
      (shouldWaitForPinnedTab && pinnedTabLoading && !pinnedTab) ||
      !activeTab ? (
        <div className="py-4 text-center">
          <Spinner size={32} />
        </div>
      ) : (
        <Tabs
          value={activeTab}
          className="w-full"
          onValueChange={handleTabChange}
        >
          {!blockedUserData?.blocked && (
            <SurfaceSelectorRow
              className="z-10"
              activeItemKey={activeTab}
              items={profileTabItems}
              onSelectItem={handleTabChange}
              actions={renderPinnedTabSelect()}
            />
          )}

          <TabsContent value={USER_PROFILE_TAB_ITEMS.TOKEN}>
            {coinPropertiesData && userPublicKey && (
              <TokenDetails
                syncURLWithActiveTab={syncURLWithActiveTab}
                tokenDetails={coinPropertiesData}
                coinPublicKey={userPublicKey}
                userTokenSubTab={userTokenSubTab}
              />
            )}
          </TabsContent>

          <TabsContent value={USER_PROFILE_TAB_ITEMS.FEED}>
            <div className="min-h-safe-screen w-full">
              {!blockedUserData?.blocked && <UserFeedHeader />}

              {renderFeedContent()}
            </div>
          </TabsContent>
          <TabsContent value={USER_PROFILE_TAB_ITEMS.MEDIA}>
            {userPublicKey && (
              <FeedMedia
                {...{
                  userPublicKey,
                }}
              />
            )}
          </TabsContent>
          <TabsContent value={USER_PROFILE_TAB_ITEMS.COLLECTIONS}>
            {userPublicKey && (
              <CollectionsPage
                userPublicKey={userPublicKey}
                username={username}
              />
            )}
          </TabsContent>
        </Tabs>
      )}
    </FeedProviderInternal>
  );
};
